import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { useCronJobs } from '@/hooks/useAdminMonitoring';
import { Skeleton } from '@/components/ui/Skeleton';
import type { CronJob } from '@/types/admin.types';
import { formatDateTime } from '@/utils/formatters';
import { Link } from 'react-router-dom';

const HEALTH_PRESENTATION: Record<
  CronJob['health'],
  { label: string; variant: 'success' | 'danger' | 'warning' }
> = {
  healthy: { label: 'Observée', variant: 'success' },
  late: { label: 'En retard', variant: 'danger' },
  unknown: { label: 'État inconnu', variant: 'warning' },
};

export function CronJobsStatus() {
  const { data, isLoading, isError } = useCronJobs();
  const jobs = data?.jobs || [];
  const healthyCount = jobs.filter((job) => job.health === 'healthy').length;

  return (
    <Card>
      <CardHeader
        title="Tâches planifiées"
        description={
          data
            ? `${healthyCount}/${jobs.length} observée(s) sur cette instance`
            : 'État réel du planificateur de cette instance'
        }
      />
      <CardContent>
        {isLoading ? (
          <div
            className="space-y-2"
            aria-label="Chargement des tâches planifiées"
          >
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700"
          >
            La supervision des tâches planifiées est indisponible. Aucun état
            n’est supposé.
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
            Aucune tâche planifiée n’est enregistrée sur cette instance.
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job.name}
                className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-800/40"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      {job.name}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      {job.reason}
                    </p>
                  </div>
                  <Badge variant={HEALTH_PRESENTATION[job.health].variant}>
                    {HEALTH_PRESENTATION[job.health].label}
                  </Badge>
                </div>
                <dl className="mt-3 grid gap-2 text-xs text-neutral-500 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="font-medium text-neutral-600 dark:text-neutral-300">
                      Dernier passage observé
                    </dt>
                    <dd>
                      {job.lastObservedAt
                        ? formatDateTime(job.lastObservedAt)
                        : 'Non observé'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-neutral-600 dark:text-neutral-300">
                      Prochain passage attendu
                    </dt>
                    <dd>
                      {job.nextExpectedAt
                        ? formatDateTime(job.nextExpectedAt)
                        : 'Inconnu'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-neutral-600 dark:text-neutral-300">
                      Planification
                    </dt>
                    <dd className="break-all font-mono">
                      {job.schedule ?? 'Inconnue'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-neutral-600 dark:text-neutral-300">
                      Source
                    </dt>
                    <dd>
                      Registre NestJS · {job.timeZone ?? 'fuseau inconnu'}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}

            <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 p-4 text-sm dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium text-neutral-800 dark:text-neutral-100">
                  Incidents d’automatisation sur 24 heures
                </p>
                <p className="text-xs text-neutral-500">
                  {data?.incidents.availability === 'available'
                    ? `${data.incidents.countLast24Hours ?? 0} incident(s) nécessitant un examen`
                    : (data?.incidents.reason ??
                      'Synthèse indisponible : état inconnu.')}
                </p>
              </div>
              <Link
                className="inline-flex min-h-11 items-center text-sm font-medium text-primary-600 hover:underline"
                to="/automations"
              >
                Voir le suivi
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
